"use client";

import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createTransfer, getTransfers, reset } from "@/state/transfers/transfer.slice";
import { getProducts } from "@/state/products/product.slice";
import { getShops } from "@/state/shops/shop.slice";
import { useToast } from "@/components/ui/use-toast";
import { usePermission } from "@/hooks/usePermission";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card } from "@/components/ui/card";
import {
  ArrowsLeftRight,
  Plus,
  Trash,
  MagnifyingGlass,
  Eye,
} from "@phosphor-icons/react";
import { Loader2, ChevronLeft, ChevronRight, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";

export default function TransfersPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const canManageStock = usePermission("Stock");

  const { transfers, pagination, isLoading, success, error } = useSelector(
    (state) => state.transfers
  );
  const { products } = useSelector((state) => state.product);
  const { shops } = useSelector((state) => state.shop);

  // List state
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Create dialog state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [fromShopId, setFromShopId] = useState("");
  const [toShopId, setToShopId] = useState("");
  const [notes, setNotes] = useState("");
  const [transferItems, setTransferItems] = useState([]);

  // Product search for adding items
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [itemQuantity, setItemQuantity] = useState("");

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  // Fetch transfers
  useEffect(() => {
    dispatch(getTransfers({ page, limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  // Load shops and products for the create form
  useEffect(() => {
    dispatch(getShops());
    dispatch(getProducts());
  }, [dispatch]);

  // Handle success/error
  useEffect(() => {
    if (success) {
      toast({ title: "Success", description: "Transfer completed successfully" });
      setIsCreateOpen(false);
      resetForm();
      dispatch(reset());
      dispatch(getTransfers({ page, limit, search: debouncedSearch }));
    }
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error });
      dispatch(reset());
    }
  }, [success, error]);

  const resetForm = useCallback(() => {
    setFromShopId("");
    setToShopId("");
    setNotes("");
    setTransferItems([]);
    setSelectedProductId(null);
    setItemQuantity("");
  }, []);

  const handleAddItem = useCallback(() => {
    if (!selectedProductId || !itemQuantity || parseInt(itemQuantity) <= 0) {
      toast({ variant: "destructive", title: "Error", description: "Select a product and enter a valid quantity" });
      return;
    }

    // Prevent duplicate products
    if (transferItems.some((i) => i.product_id === selectedProductId)) {
      toast({ variant: "destructive", title: "Error", description: "Product already added" });
      return;
    }

    const product = products?.find((p) => p.id === selectedProductId);
    setTransferItems((prev) => [
      ...prev,
      {
        product_id: selectedProductId,
        product_title: product?.title || `Product #${selectedProductId}`,
        quantity: parseInt(itemQuantity),
      },
    ]);
    setSelectedProductId(null);
    setItemQuantity("");
  }, [selectedProductId, itemQuantity, transferItems, products, toast]);

  const handleRemoveItem = useCallback((productId) => {
    setTransferItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const handleSubmitTransfer = useCallback(() => {
    if (!fromShopId || !toShopId) {
      toast({ variant: "destructive", title: "Error", description: "Select source and destination shops" });
      return;
    }
    if (fromShopId === toShopId) {
      toast({ variant: "destructive", title: "Error", description: "Source and destination must be different" });
      return;
    }
    if (transferItems.length === 0) {
      toast({ variant: "destructive", title: "Error", description: "Add at least one item" });
      return;
    }

    dispatch(
      createTransfer({
        from_shop_id: parseInt(fromShopId),
        to_shop_id: parseInt(toShopId),
        notes,
        items: transferItems.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
      })
    );
  }, [dispatch, fromShopId, toShopId, notes, transferItems, toast]);

  const handleViewTransfer = useCallback((transfer) => {
    setSelectedTransfer(transfer);
    setDetailOpen(true);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredProducts = products?.filter((p) =>
    p.title?.toLowerCase().includes(productSearchTerm.toLowerCase())
  ) || [];

  return (
    <div className="mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ArrowsLeftRight className="h-8 w-8" />
            Stock Transfers
          </h1>
          <p className="text-muted-foreground mt-1">
            Transfer stock between shops
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2 bg-white dark:bg-slate-900 px-2 border w-fit">
            <MagnifyingGlass className="w-5 h-5 text-gray-500" />
            <Input
              placeholder="Search by reference..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="border-none shadow-none focus-visible:ring-0 w-[200px]"
            />
          </div>
          {canManageStock && (
            <Button
              onClick={() => {
                resetForm();
                setIsCreateOpen(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Transfer
            </Button>
          )}
        </div>
      </div>

      {/* Transfers Table */}
      <Card className="enterprise-table-container">
        <div>
          {isLoading && transfers.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Transferred By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center h-32 text-muted-foreground"
                    >
                      No transfers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  transfers.map((transfer) => (
                    <TableRow key={transfer.id}>
                      <TableCell className="font-mono font-medium">
                        {transfer.reference_number}
                      </TableCell>
                      <TableCell>{transfer.fromShop?.name || "-"}</TableCell>
                      <TableCell>{transfer.toShop?.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {transfer.items?.length || 0} item(s)
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {transfer.transferredBy?.first_name || transfer.transferredBy?.username || "-"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(transfer.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewTransfer(transfer)}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.pages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Transfer Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Stock Transfer</DialogTitle>
            <DialogDescription>
              Transfer stock from one shop to another. Select source, destination, and add products.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Shop Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Shop *</Label>
                <Select value={fromShopId} onValueChange={setFromShopId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Source shop" />
                  </SelectTrigger>
                  <SelectContent>
                    {shops?.map((shop) => (
                      <SelectItem key={shop.id} value={String(shop.id)}>
                        {shop.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To Shop *</Label>
                <Select value={toShopId} onValueChange={setToShopId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Destination shop" />
                  </SelectTrigger>
                  <SelectContent>
                    {shops
                      ?.filter((s) => String(s.id) !== fromShopId)
                      .map((shop) => (
                        <SelectItem key={shop.id} value={String(shop.id)}>
                          {shop.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Item Row */}
            <div className="space-y-2">
              <Label>Add Products</Label>
              <div className="flex gap-2">
                <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="flex-1 justify-between"
                    >
                      {selectedProductId
                        ? products?.find((p) => p.id === selectedProductId)?.title || "Select product"
                        : "Select product"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0">
                    <Command>
                      <CommandInput
                        placeholder="Search products..."
                        value={productSearchTerm}
                        onValueChange={setProductSearchTerm}
                      />
                      <CommandEmpty>No products found.</CommandEmpty>
                      <CommandGroup className="max-h-[200px] overflow-y-auto">
                        {filteredProducts.slice(0, 50).map((product) => (
                          <CommandItem
                            key={product.id}
                            value={product.title}
                            onSelect={() => {
                              setSelectedProductId(product.id);
                              setProductSearchOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedProductId === product.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {product.title}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Input
                  type="number"
                  placeholder="Qty"
                  value={itemQuantity}
                  onChange={(e) => setItemQuantity(e.target.value)}
                  className="w-[100px]"
                  min="1"
                />
                <Button type="button" onClick={handleAddItem} variant="secondary">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Items Table */}
            {transferItems.length > 0 && (
              <div className="border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-[100px]">Quantity</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transferItems.map((item) => (
                      <TableRow key={item.product_id}>
                        <TableCell className="font-medium">
                          {item.product_title}
                        </TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(item.product_id)}
                            className="text-red-500 hover:text-red-600 h-8 w-8"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this transfer..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitTransfer}
              disabled={isLoading || !fromShopId || !toShopId || transferItems.length === 0}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Transferring...
                </>
              ) : (
                "Complete Transfer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="font-mono">
              {selectedTransfer?.reference_number}
            </DialogTitle>
            <DialogDescription>
              Transfer details
            </DialogDescription>
          </DialogHeader>

          {selectedTransfer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">From</p>
                  <p className="font-medium">{selectedTransfer.fromShop?.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">To</p>
                  <p className="font-medium">{selectedTransfer.toShop?.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Transferred By</p>
                  <p className="font-medium">
                    {selectedTransfer.transferredBy?.first_name
                      ? `${selectedTransfer.transferredBy.first_name} ${selectedTransfer.transferredBy.last_name || ""}`
                      : selectedTransfer.transferredBy?.username || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(selectedTransfer.createdAt)}</p>
                </div>
              </div>

              {selectedTransfer.notes && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Notes</p>
                  <p>{selectedTransfer.notes}</p>
                </div>
              )}

              <div className="border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTransfer.items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.Product?.title || `Product #${item.product_id}`}
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
